import React from 'react';
import { Descriptions, Empty, Tag, Tooltip } from 'antd';
import { formatDateAndTime } from '../../../utils/formatDateTime';
import { EMPTY_STATES, FIELD_LIMITS } from '../constants';
import { formatCurrency } from '../utils/formatters';
import { formatAllowance } from '../utils/terms';
import { SectionTitle } from '../Styles';
import type { CreatorTerms } from '../types';

interface CurrentTermsPanelProps {
  terms?: CreatorTerms;
  storeNamesById: Record<string, string>;
}

const formatDate = (iso?: string): string =>
  iso ? formatDateAndTime(iso).formattedDate : '—';

/**
 * Read-only view of the creator's currently-active terms.
 *
 * Shown above the "set new terms" form so it's obvious what is being replaced —
 * the form creates a new version rather than editing this one.
 */
const CurrentTermsPanel: React.FC<CurrentTermsPanelProps> = ({ terms, storeNamesById }) => {
  if (!terms) {
    return (
      <>
        <SectionTitle>Current Terms</SectionTitle>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <>
              {EMPTY_STATES.TERMS}
              <div style={{ fontSize: 12, marginTop: 4 }}>
                A creator needs active terms before they can create any discount code.
              </div>
            </>
          }
        />
      </>
    );
  }

  const storeIds = terms.storeIds ?? [];
  const allStores = storeIds.length === 0;

  return (
    <>
      <SectionTitle>Current Terms</SectionTitle>
      <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small" style={{ marginTop: 12 }}>
        <Descriptions.Item label="Allowance">
          <strong>{formatAllowance(terms)}</strong>{' '}
          <Tag>{terms.allowanceType}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Active period">
          {formatDate(terms.startsAt)} → {formatDate(terms.endsAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Min. order subtotal">
          {terms.minSubtotal != null ? formatCurrency(terms.minSubtotal) : 'No minimum'}
        </Descriptions.Item>
        <Descriptions.Item label="Max active codes">
          {terms.maxActiveCodes ?? FIELD_LIMITS.DEFAULT_MAX_ACTIVE_CODES}
        </Descriptions.Item>
        <Descriptions.Item label="Max customer share">
          {terms.maxCustomerShare != null ? formatCurrency(terms.maxCustomerShare) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Max creator share">
          {terms.maxCreatorShare != null ? formatCurrency(terms.maxCreatorShare) : '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Stores" span={2}>
          {allStores ? (
            <Tag color="blue">All stores</Tag>
          ) : (
            storeIds.map((id) => (
              <Tooltip key={id} title={id}>
                <Tag>{storeNamesById[id] || id}</Tag>
              </Tooltip>
            ))
          )}
        </Descriptions.Item>
      </Descriptions>
    </>
  );
};

/**
 * Memoised: re-renders only when its own props change, not whenever the parent
 * screen re-renders for an unrelated filter or fetch.
 */
export default React.memo(CurrentTermsPanel);
